import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, MessageCircle, Phone } from 'lucide-react';
import { Card, Pill } from '@nako/ui';
import { supabase } from '@/lib/supabase';

interface RecallRow {
  visit_id: string;
  patient_id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  follow_up_at: string;
  visit_type: string;
}

interface Props {
  businessId: string;
  /** Practice display name — used in the recall message. */
  practiceName: string;
  /** How far ahead to consider a recall. Defaults to 14 days per audit Part 4 §3. */
  daysAhead?: number;
  /** Defaults to 'en'. */
  lang?: 'en' | 'tn' | 'zu' | 'ha' | 'pcm' | 'sn';
}

/**
 * Upcoming Recalls — dashboard widget for healthcare businesses.
 *
 * Lists patient visits whose `follow_up_at` falls within the next
 * `daysAhead` days. Each row has a one-click "WhatsApp" deeplink that
 * pre-fills a friendly local-language reminder, and a "Call" tel:
 * fallback if no WhatsApp number is available.
 *
 * Why query as two steps (visits then patients): we need RLS-correct
 * filtering by business_id on the patient_visits table, and the join
 * lookup happens client-side once we have the patient IDs. Avoids a
 * SECURITY DEFINER view + a custom RPC for a feature this small.
 */
export function RecallWidget({ businessId, practiceName, daysAhead = 14, lang = 'en' }: Props) {
  const [rows, setRows] = useState<RecallRow[] | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const today = new Date();
      const horizon = new Date(today);
      horizon.setDate(horizon.getDate() + daysAhead);
      const todayIso = today.toISOString().slice(0, 10);
      const horizonIso = horizon.toISOString().slice(0, 10);

      const visitsResp = await supabase
        .from('patient_visits')
        .select('id, patient_id, follow_up_at, visit_type')
        .eq('business_id', businessId)
        .gte('follow_up_at', todayIso)
        .lte('follow_up_at', horizonIso)
        .order('follow_up_at', { ascending: true })
        .limit(10);

      const visits =
        (visitsResp.data as Array<{
          id: string;
          patient_id: string;
          follow_up_at: string;
          visit_type: string;
        }>) ?? [];
      if (visits.length === 0) {
        if (active) setRows([]);
        return;
      }

      const ids = Array.from(new Set(visits.map((v) => v.patient_id)));
      const patientsResp = await supabase
        .from('patients')
        .select('id, first_name, last_name, phone')
        .in('id', ids);
      const patientsById = new Map<
        string,
        { first_name: string; last_name: string; phone: string | null }
      >((patientsResp.data ?? []).map((p) => [p.id as string, p]));

      const merged: RecallRow[] = visits.map((v) => {
        const p = patientsById.get(v.patient_id);
        return {
          visit_id: v.id,
          patient_id: v.patient_id,
          follow_up_at: v.follow_up_at,
          visit_type: v.visit_type,
          first_name: p?.first_name ?? 'Patient',
          last_name: p?.last_name ?? '',
          phone: p?.phone ?? null,
        };
      });
      if (active) setRows(merged);
    })();
    return () => {
      active = false;
    };
  }, [businessId, daysAhead]);

  if (rows === null) {
    return (
      <Card>
        <h2 className="font-display mb-3 flex items-center gap-2 text-lg">
          <CalendarClock size={18} /> Upcoming recalls
        </h2>
        <p className="text-muted text-sm">Loading…</p>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <h2 className="font-display mb-3 flex items-center gap-2 text-lg">
          <CalendarClock size={18} /> Upcoming recalls
        </h2>
        <p className="text-muted text-sm">No follow-ups in the next {daysAhead} days.</p>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="font-display mb-1 flex items-center gap-2 text-lg">
        <CalendarClock size={18} /> Upcoming recalls
      </h2>
      <p className="text-muted mb-3 text-xs">
        Patients due in the next {daysAhead} days — one click sends a friendly WhatsApp reminder.
      </p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <RecallRowItem key={r.visit_id} row={r} practiceName={practiceName} lang={lang} />
        ))}
      </ul>
    </Card>
  );
}

function RecallRowItem({
  row,
  practiceName,
  lang,
}: {
  row: RecallRow;
  practiceName: string;
  lang: NonNullable<Props['lang']>;
}) {
  const fullName = `${row.first_name} ${row.last_name}`.trim();
  const dueText = useMemo(() => formatDue(row.follow_up_at), [row.follow_up_at]);
  const whatsAppHref = useMemo(
    () =>
      buildWhatsAppLink(row.phone, recallMessage({ first: row.first_name, practiceName, lang })),
    [row.phone, row.first_name, practiceName, lang],
  );
  const telHref = row.phone ? `tel:${row.phone.replace(/\s+/g, '')}` : null;

  return (
    <li className="border-line flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{fullName}</div>
        <div className="text-muted flex items-center gap-2 text-xs">
          <span className="capitalize">{row.visit_type.replace('_', ' ')}</span>
          <span>·</span>
          <Pill tone="accent" className="px-2 py-0">
            {dueText}
          </Pill>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {whatsAppHref && (
          <a
            href={whatsAppHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`WhatsApp ${fullName}`}
            className="text-muted hover:text-ink rounded-full p-2 hover:bg-emerald-50"
            title="Send WhatsApp reminder"
          >
            <MessageCircle size={16} />
          </a>
        )}
        {telHref && (
          <a
            href={telHref}
            aria-label={`Call ${fullName}`}
            className="text-muted hover:text-ink rounded-full p-2 hover:bg-sky-50"
            title="Call patient"
          >
            <Phone size={16} />
          </a>
        )}
      </div>
    </li>
  );
}

/** Friendly date pill: "Today", "Tomorrow", "In 5 days", "Wed 28 May". */
function formatDue(iso: string): string {
  const due = new Date(iso + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `In ${diffDays} days`;
  return due.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Localised recall message — short, friendly, name-personalised. */
function recallMessage({
  first,
  practiceName,
  lang,
}: {
  first: string;
  practiceName: string;
  lang: NonNullable<Props['lang']>;
}): string {
  switch (lang) {
    case 'tn':
      return `Dumela ${first}, ke kgakololo ya gore o boele kwa ${practiceName} bakeng sa go tlhatlhobiwa. Re itumelela go go bona gape!`;
    case 'zu':
      return `Sawubona ${first}, sikukhumbuza ngomhlangano wakho e-${practiceName}. Sijabule ukukubona futhi!`;
    case 'ha':
      return `Sannu ${first}, wannan tunatarwa ne don alƙawarinka a ${practiceName}. Mun yi maraba da ganinka!`;
    case 'pcm':
      return `How far ${first}, na to remind you say you get appointment for ${practiceName}. We dey wait to see you!`;
    case 'en':
    default:
      return `Hello ${first}, this is a friendly reminder for your check-up at ${practiceName}. We look forward to seeing you again!`;
  }
}

/** Build a wa.me link if the phone looks workable; else null. */
function buildWhatsAppLink(phone: string | null, message: string): string | null {
  if (!phone) return null;
  // Strip everything except digits + a leading +.
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.length < 7) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}
